import { RefObject } from 'react';
import { IModalFormField } from '@components-member/socialTrading/social.interfaces';
import ENUM_CONSTANTS from '@components-shared/enum.constants';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { EnumUSTFormField } from '@components-shared/socialTrading/enums';
import { RecommendedMinDepositLimitsType } from '@components-shared/socialTrading/interfaces';
import { isLotCopyTypeCheck, minDepositLimitsAreConfigured } from '@components-shared/socialTrading/utils';
import { keyMap } from '@locales/lokalise/keyMap';
import { getFormattedNumber, getUpperFormattedInteger } from '@utils/helper';
import { reactRouterService } from '@utils/library/reactRouter.service';
import { IApiCopyType, IApiGroupMappingType, IApiUserTradingAccountType } from 'api/interfaces/data-contracts';
import CONSTANTS from 'components/shared/constants';
import { IFormField } from 'components/shared/forms/formField.interface';
import { IMaster } from 'components/user/socialTrading/social.interfaces';
import { CLIENT_SOURCE_TYPE, MASTERS_RATING_ACTION_SOURCE, QUERY_PARAMS } from 'global.enums';
import { ClientSourceType } from 'global.interfaces';
import i18next from 'i18next';
import { IMasterAccount } from './MasterProfile/types';
import styles from './styles/accent.module.scss';

export const tableRefreshHandler = async (ref: RefObject<{ updateData: () => Promise<void> }>) => {
  if (ref && ref.current) {
    await ref.current.updateData();
  }
};

export const getAccountPrefix = (
  type: string,
): (typeof CONSTANTS.ACCOUNT_TYPE_PREFIX)[keyof typeof CONSTANTS.ACCOUNT_TYPE_PREFIX] | '' => {
  const { STD, ECN } = CONSTANTS.ACCOUNT_TYPE_PREFIX;

  if (
    type === IApiGroupMappingType.RealCopyTradingMasterStandard ||
    type === IApiGroupMappingType.RealCopyTradingInvestorStandard
  ) {
    return STD;
  }

  if (
    type === IApiGroupMappingType.RealCopyTradingMasterEcn ||
    type === IApiGroupMappingType.RealCopyTradingInvestorEcn
  ) {
    return ECN;
  }

  return '';
};

export const setLotForEquityAndBalanceCopyTypeToZero = <T extends { lot?: string; copyType?: IApiCopyType }>(
  form: T,
): void => {
  if (!isLotCopyTypeCheck(form.copyType)) {
    form.lot = String(0);
  }
};

export const setMarginPercentageLevelToZero = <
  T extends { percentageLossStopCopyLevel: string; marginStopCopyLevel: string },
>(
  form: T,
) => {
  if (!form.percentageLossStopCopyLevel) {
    form.percentageLossStopCopyLevel = String(0);
  }

  if (!form.marginStopCopyLevel) {
    form.marginStopCopyLevel = String(0);
  }
};

export const getAccentColorClass = (value: string | number): string => {
  const state = Math.sign(Number(value));

  switch (state) {
    case 1:
      return styles.accent_green;

    case -1:
      return styles.accent_red;

    default:
      return styles.accent_fade;
  }
};

export const ibCodeFieldIsEnabled = (canSetIb: boolean, clientSourceType: ClientSourceType): boolean => {
  const isDirectClientSourceType = clientSourceType === CLIENT_SOURCE_TYPE.DIRECT;

  return canSetIb && !isDirectClientSourceType;
};

export const filterIbCodeFieldIfDisabled = (
  fields: (IModalFormField | IFormField)[],
  canSetIb: boolean,
  clientSourceType: ClientSourceType,
) => {
  const ibCodeField = ibCodeFieldIsEnabled(canSetIb, clientSourceType);

  if (ibCodeField) {
    return fields;
  }

  return fields.filter((field) => field.name !== 'ibCode');
};

const onMasterActionHandler = (
  actionType: QUERY_PARAMS,
  data: IMaster | IMasterAccount,
  routeData: { search: string },
) => {
  const { masterAccountId } = data;
  const sourceType = routeData.search.includes(MASTERS_RATING_ACTION_SOURCE.EXTERNAL)
    ? MASTERS_RATING_ACTION_SOURCE.EXTERNAL
    : MASTERS_RATING_ACTION_SOURCE.DEFAULT;

  reactRouterService.addUrlQueryParams({
    [QUERY_PARAMS.ACTION]: actionType,
    [QUERY_PARAMS.MASTER]: masterAccountId,
    [QUERY_PARAMS.SOURCE]: sourceType,
  });
};

export const onMasterStopHandler = (data: IMaster | IMasterAccount, routeData: { search: string }) => {
  onMasterActionHandler(QUERY_PARAMS.STOP, data, routeData);
};

export const onMasterCopyHandler = (data: IMaster | IMasterAccount, routeData: { search: string }): void => {
  onMasterActionHandler(QUERY_PARAMS.COPY, data, routeData);
};

export const setTooltipMessage = (idx: number, message: string, fields: Array<IModalFormField>): void => {
  if (idx > -1) {
    fields[idx] = Object.assign({
      ...fields[idx],
      tooltip: message,
    });
  }
};

export const getGroupMappingTypeById = (accountType: IApiUserTradingAccountType): IApiGroupMappingType | undefined => {
  return accountType ? accountType.groupMappingType : undefined;
};

export const isMasterDepositFieldsVisible = (
  recommendedMinDepositLimits: RecommendedMinDepositLimitsType[],
  groupMappingType?: IApiGroupMappingType,
): boolean => {
  if (!groupMappingType) {
    return false;
  }

  return minDepositLimitsAreConfigured(recommendedMinDepositLimits, groupMappingType);
};

export const getMasterRecommendedDepositTooltipMsg = (
  depositLimits: RecommendedMinDepositLimitsType[],
  accountCurrency: string,
  makeMinDepositAmountRequired: boolean,
  accountGroupMappingType?: IApiGroupMappingType,
): string => {
  if (!accountGroupMappingType) {
    return '';
  }
  const accountTypeLimitConfig = depositLimits.find(
    ({ groupMappingType, currency }) => groupMappingType === accountGroupMappingType && currency === accountCurrency,
  );

  const limit = accountTypeLimitConfig && accountTypeLimitConfig.limits;

  if (!limit) {
    return '';
  }

  const min = getFormattedNumber(limit.minAllowedAmount, { precision: true });
  const max = getFormattedNumber(limit.maxAllowedAmount, { precision: true });
  const mes = makeMinDepositAmountRequired
    ? keyMap.tradingAccountsOpenMasterAccountFormRecommendedDepositMandatoryTooltipMsg
    : keyMap.tradingAccountsOpenMasterAccountFormRecommendedDepositOptionalTooltipMsg;

  return i18next.t(mes, {
    min,
    max,
    currency: accountCurrency,
  });
};

export const getMasterDescription = (recommendedMinDepositAmount: number | null, currency: string | null): string => {
  if (!recommendedMinDepositAmount) {
    return '';
  }
  const amount = getUpperFormattedInteger(recommendedMinDepositAmount);

  return i18next.t(keyMap.socialTradingModalMasterDescription, { amount, currency });
};

export const copyTypeOptionsHandler = (
  availableAllocationMethods: IApiCopyType[],
  fields: IModalFormField[],
  accountAllocationMethod?: IApiCopyType,
): void => {
  const copyTypeFieldIdx = FormUtils.getFieldIndex(fields, EnumUSTFormField.copyType);

  if (availableAllocationMethods.length === 0) {
    return;
  }

  const defaultOptions = ENUM_CONSTANTS.ALLOCATION_METHOD as Array<{ value: IApiCopyType }>;

  const filteredOptions = defaultOptions.filter((defaultOption) =>
    availableAllocationMethods.includes(defaultOption.value),
  );

  if (accountAllocationMethod && !availableAllocationMethods.includes(accountAllocationMethod)) {
    const allocationMethodEnumValue = defaultOptions.find((option) => option.value === accountAllocationMethod);

    if (allocationMethodEnumValue) {
      filteredOptions.push(allocationMethodEnumValue);
    }
  }

  FormUtils.updateFormField(copyTypeFieldIdx, { options: [...filteredOptions] }, fields);
};

export const getMasterCopyType = (availableAllocationMethods: IApiCopyType[], value: IApiCopyType) => {
  return availableAllocationMethods.includes(value) ? value : undefined;
};

export const getLeverageFromInvestorType = (
  investorTypes: IApiUserTradingAccountType[],
  selectedInvestorType: number | IApiGroupMappingType | undefined,
) => {
  const investorType = investorTypes.find(
    (type) => type.id === selectedInvestorType || type.groupMappingType === selectedInvestorType,
  );

  if (investorType) {
    return investorType.leverages.map((option) => ({ value: option }));
  }
};
